import { NO, UITextView, UIView, YES } from "uicore-ts"
import { CBDataViewCellDescriptorWithObject } from "../../Custom components/CB/CBDataView"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import {
    RHButtons,
    RHSpacing
} from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCompanyMembershipWorkflow } from "../../SocketClientFunctions"
import { CompanyRoleBadgesView } from "../CompanyAdministration/CompanyRoleBadgesView"
import { CompanyAffiliationViewController } from "./CompanyAffiliationViewController"


type CompanyAffiliationWorkflowTableRow = RHCompanyMembershipWorkflow & {
    companySearchText: string
    roleSearchText: string
    statusText: string
    typeText: string
}


class CompanyAffiliationWorkflowActionsView extends UIView {

    readonly secondaryButton = new RHButton().configuredWithObject({
        titleLabel: {
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    readonly primaryButton = new RHButton().configuredWithObject({
        titleLabel: {
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.tableActionVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    secondaryWasPressed?: () => void
    primaryWasPressed?: () => void


    constructor(elementID?: string) {
        super(elementID)
        this.secondaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.secondaryWasPressed?.()
        }
        this.primaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.primaryWasPressed?.()
        }
        this.secondaryButton.titleLabel.textAlignment = UITextView.textAlignment.center
        this.primaryButton.titleLabel.textAlignment = UITextView.textAlignment.center
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const visibleButtons = [this.secondaryButton, this.primaryButton].filter(
            button => !button.hidden
        )
        this.contentBounds
            .rectangleWithHeight(RHButtons.tableActionHeight, 0.5)
            .framesByDistributingViewsAsRow(
                visibleButtons,
                RHSpacing.small,
                visibleButtons.map(() => RHButtons.tableActionWidth),
                0.5
            )
    }


    override intrinsicContentHeight() {
        return RHButtons.tableActionHeight
    }

}


export class CompanyAffiliationWorkflowsView extends UIView {

    readonly dataView = new RHDataView<CompanyAffiliationWorkflowTableRow>()
        .configuredWithObject({
            noDataMessage: "Taotlusi ega kutseid ei ole",
            hasResizableColumns: NO,
            tableView: { allRowsHaveEqualHeight: YES }
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        this.dataView.searchTextField,
        this.dataView._gridContainerView
    ])

    invitationWasAccepted?: (workflow: RHCompanyMembershipWorkflow) => void
    invitationWasRejected?: (workflow: RHCompanyMembershipWorkflow) => void
    membershipRequestWasCancelled?: (workflow: RHCompanyMembershipWorkflow) => void


    constructor(elementID?: string) {
        super(elementID)
        this.dataView.searchTextField.placeholderText =
            "Otsi ettevõtte, tüübi, staatuse või rolli järgi..."
        this.dataView.descriptors = this._descriptors()
    }


    set workflows(workflows: RHCompanyMembershipWorkflow[]) {
        this.dataView.data = workflows.map(workflow => ({
            ...workflow,
            companySearchText:
                `${workflow.company.name} ${workflow.company.businessRegistryCode}`,
            roleSearchText: CompanyRoleBadgesView
                .displayLabelsForRoles(workflow.requestedRoles)
                .join(", "),
            statusText: CompanyAffiliationViewController.workflowStatusText(workflow),
            typeText: CompanyAffiliationViewController.workflowTypeText(workflow)
        }))
        this.setNeedsLayout()
    }


    _descriptors() {
        return [
            CBDataViewCellDescriptorWithObject<CompanyAffiliationWorkflowTableRow>({
                title: "Tüüp",
                keyPath: "typeText",
                allowSorting: YES,
                weight: 0.8
            }),
            CBDataViewCellDescriptorWithObject<CompanyAffiliationWorkflowTableRow>({
                title: "Ettevõte",
                keyPath: "companySearchText",
                allowSorting: YES,
                weight: 1.2,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(dataItem.company.name)
                }
            }),
            CBDataViewCellDescriptorWithObject<
                CompanyAffiliationWorkflowTableRow,
                CompanyRoleBadgesView
            >({
                title: "Roll",
                keyPath: "roleSearchText",
                allowSorting: NO,
                weight: 1.6,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyRoleBadgesView(),
                updateCell: (cellView, dataItem) => {
                    cellView.customView.updateWithRoles(
                        dataItem.requestedRoles,
                        dataItem.isEligibleToActAsProcurer
                    )
                }
            }),
            CBDataViewCellDescriptorWithObject<CompanyAffiliationWorkflowTableRow>({
                title: "Staatus",
                keyPath: "statusText",
                allowSorting: YES,
                weight: 2
            }),
            CBDataViewCellDescriptorWithObject<
                CompanyAffiliationWorkflowTableRow,
                CompanyAffiliationWorkflowActionsView
            >({
                title: "Tegevused",
                keyPath: "statusText",
                allowSorting: NO,
                weight: 0,
                minimumWidth: RHButtons.tableActionWidth * 2 + RHSpacing.small,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyAffiliationWorkflowActionsView(),
                updateCell: (cellView, dataItem) => {
                    this._configureActionsView(cellView.customView, dataItem)
                }
            })
        ]
    }


    _configureActionsView(
        actionsView: CompanyAffiliationWorkflowActionsView,
        workflow: RHCompanyMembershipWorkflow
    ) {
        actionsView.primaryButton.hidden = YES
        actionsView.secondaryButton.hidden = YES
        actionsView.primaryWasPressed = undefined
        actionsView.secondaryWasPressed = undefined

        if (workflow.status !== "pending" || workflow.kind === "registration") {
            actionsView.setNeedsLayout()
            return
        }

        actionsView.secondaryButton.hidden = NO
        if (workflow.direction === "company_to_user") {
            actionsView.primaryButton.hidden = NO
            actionsView.primaryButton.titleLabel.text = "Võta vastu"
            actionsView.secondaryButton.titleLabel.text = "Lükka tagasi"
            actionsView.primaryWasPressed = () => {
                this.invitationWasAccepted?.(workflow)
            }
            actionsView.secondaryWasPressed = () => {
                this.invitationWasRejected?.(workflow)
            }
        }
        else {
            actionsView.secondaryButton.titleLabel.text = "Tühista"
            actionsView.secondaryWasPressed = () => {
                this.membershipRequestWasCancelled?.(workflow)
            }
        }
        actionsView.setNeedsLayout()
    }


    override layoutSubviews() {
        super.layoutSubviews()
        this.dataView.frame = this.contentBounds
    }

}
